import { useIntl } from 'react-intl';
import { createColumnHelper } from '@tanstack/react-table';
import { useModelService } from 'services/model/model.service';
import { PriceChange } from 'pages/trading/model';
import { CellValueExporters, HeaderExporters, numberCellExporter } from 'components/table/DataTableExportUtil';
import { useMeasureFormatter } from 'components/measure-unit/measureFormatter';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { usePriceChangeMeasures } from 'pages/trading/price/change/priceChangeMeasures';
import { useDataEntriesInTables } from 'components/table/useDataEntriesInTables';
import { formatValueAsPositiveOrNegative } from 'components/table/DataTableUtil';

export interface PriceBreakdownTableRow {
  id: string;
  artImageUrl: string;
  prdName: string;
  styBrandColor: string;
  chnName: string;
  oldPrice: number;
  newPrice: number;
  priceChangeDelta: number;
  promoPrice?: number;
  promoPriceChangeDelta?: number;
  dailyGmv: number;
  oldDailyGmv: number;
  dailyGmvDelta: number;
  dailyQty: number;
  dailyQtyDelta: number;
  oldDailyQty: number;
  originalData: PriceChange;
}

export const usePriceChangeTableColumns = (containsCM1: boolean) => {
  const intl = useIntl();

  const priceChangeMeasures = usePriceChangeMeasures();
  const { getAttributes, getMeasures } = useModelService(priceChangeMeasures);

  const columnHelper = createColumnHelper<PriceBreakdownTableRow>();

  const [prdName, chnName, artImageUrl, styBrandColor] = getAttributes([
    'prdName',
    'chnName',
    'artImageUrl',
    'styBrandColor',
  ]);

  const [
    oldPrice,
    newPrice,
    priceChangeDelta,
    dailyQty,
    dailyQtyDelta,
    dailyGmv,
    dailyGmvDelta,
    cm1,
    cm1Delta,
    cm1ValExtExpOD,
  ] = getMeasures([
    'oldPrice',
    'newPrice',
    'priceChangeDelta',
    'dailyQty',
    'dailyQtyDelta',
    'dailyGmv',
    'dailyGmvDelta',
    'totalCM1',
    'cm1Delta',
    'cm1ValExtExpOD',
  ]);

  const { createAttributeColumn, createMeasureColumn } = useDataEntriesInTables();

  const columns = [
    createAttributeColumn(artImageUrl),
    createAttributeColumn(prdName),
    createAttributeColumn(styBrandColor),
    createAttributeColumn(chnName),

    columnHelper.group({
      id: 'Price',
      header: intl.formatMessage({ id: 'trading.price_change.table.price_header' }),
      meta: { skipExport: true },
      columns: [
        createMeasureColumn(oldPrice, {
          meta: {
            sortTitleId: 'trading.price_change.table.price_header',
            sortSubtitleId: 'trading.price_change.table.old',
          },
        }),
        createMeasureColumn(newPrice, {
          meta: {
            sortTitleId: 'trading.price_change.table.price_header',
            sortSubtitleId: 'trading.price_change.table.new',
          },
        }),
        createMeasureColumn(priceChangeDelta),
      ],
    }),

    columnHelper.group({
      id: 'ItemsPerDay',
      header: intl.formatMessage({ id: 'trading.price_change.table.daily_items' }),
      columns: [
        createMeasureColumn(dailyQty, {
          meta: {
            sortTitleId: 'trading.price_change.table.daily_items',
            sortSubtitleId: 'trading.price_change.table.new_price_label',
          },
        }),
        createMeasureColumn(dailyQtyDelta, {
          header: { mainHeaderId: 'trading.price_change.table.delta' },
          signedValue: true,
          className: row => formatValueAsPositiveOrNegative(row.getValue(dailyQtyDelta.code)),
          meta: {
            sortTitleId: 'trading.price_change.table.daily_items',
            sortSubtitleId: 'trading.price_change.table.delta',
          },
        }),
      ],
    }),

    columnHelper.group({
      id: 'GmvPerDay',
      header: intl.formatMessage({ id: 'trading.price_change.table.daily_gmv' }),
      columns: [
        createMeasureColumn(dailyGmv, {
          meta: {
            sortTitleId: 'trading.price_change.table.daily_gmv',
            sortSubtitleId: 'trading.price_change.table.new_price_label',
          },
        }),
        createMeasureColumn(dailyGmvDelta, {
          header: { mainHeaderId: 'trading.price_change.table.delta' },
          signedValue: true,
          className: row => formatValueAsPositiveOrNegative(row.getValue(dailyGmvDelta.code)),
        }),
      ],
    }),
  ];

  if (containsCM1) {
    columns.push(
      columnHelper.group({
        id: 'CM1',
        header: cm1ValExtExpOD.longName,
        columns: [
          createMeasureColumn(cm1, {
            meta: {
              sortTitleId: 'trading.price_change.table.cm1',
              sortSubtitleId: 'trading.price_change.table.new_price_label',
            },
          }),
          createMeasureColumn(cm1Delta, {
            header: { mainHeaderId: 'trading.price_change.table.delta' },
            signedValue: true,
            className: row => formatValueAsPositiveOrNegative(row.getValue(cm1Delta.code)),
          }),
        ],
      })
    );
  }

  return columns;
};

export const useCustomHeaderExporters = () => {
  return {
    price: intl =>
      `${intl.formatMessage({ id: 'trading.price_change.table.price_header' })} (${intl.formatMessage({ id: 'trading.price_change.table.old_new' })})`,
    priceChange: intl =>
      intl.formatMessage({
        id: 'trading.price_change.table.price_change_percent',
      }),
    dailyGmv: intl =>
      `${intl.formatMessage({ id: 'trading.price_change.table.daily_gmv' })} (${intl.formatMessage({ id: 'trading.price_change.table.new_price_label' })})`,
    dailyGmvDelta: intl =>
      intl.formatMessage({
        id: 'trading.price_change.table.daily_gmv_delta_percent',
      }),
    dailyItems: intl =>
      `${intl.formatMessage({ id: 'trading.price_change.table.daily_items' })} (${intl.formatMessage({ id: 'trading.price_change.table.new_price_label' })})`,
    dailyItemsDelta: intl =>
      intl.formatMessage({
        id: 'trading.price_change.table.daily_items_delta_percent',
      }),
  } as HeaderExporters;
};

export const useCustomCellValueExporters = () => {
  const { measureFormatter } = useMeasureFormatter();

  return {
    price: numberCellExporter,
    dailyGmv: numberCellExporter,
    dailyItems: numberCellExporter,
    priceChange: (val: unknown) =>
      measureFormatter((val as number) / 100, MeasureUnitCode.PERCENT, { isDiff: true, decimalPlaces: 2 }),
    dailyGmvDelta: (val: unknown) =>
      measureFormatter((val as number) / 100, MeasureUnitCode.PERCENT, { isDiff: true, decimalPlaces: 2 }),
    dailyItemsDelta: (val: unknown) =>
      measureFormatter((val as number) / 100, MeasureUnitCode.PERCENT, { isDiff: true, decimalPlaces: 2 }),
  } as CellValueExporters;
};
